import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import AmazonCredentialsForm from "@via/pages/account/profile/credentials/credential-key-form/amazon/amazon-credentials-form.tsx";
import GoogleCredentialsForm from "@via/pages/account/profile/credentials/credential-key-form/google/google-credentials-form.tsx";
import SSHCredentialsForm from "@via/pages/account/profile/credentials/credential-key-form/ssh/ssh-credentials-form.tsx";
import {
  cancelGeneratedSSHKey,
  fetchGeneratedSSHKeys,
  useCreateCredential,
} from "@via/pages/account/profile/profile.api.tsx";
import {
  CREDENTIAL_TYPES,
  CredentialTypeOptions,
  USER_ROLES,
} from "@via/utils/constants.tsx";
import { removeEmptyStrings } from "@via/utils/helpers.tsx";
import { CredentialKeys } from "@via/utils/interfaces.tsx";
import {
  GeneratePendingSshKeyResponse,
  UpsertCredentialsRequestBody,
  UpsertCredentialsResponse,
} from "common";
import { JSX, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

export default function CredentialKeyForm({
  setOpen,
  givenCredentialType,
  onSuccess,
}: {
  setOpen: (open: boolean) => void;
  givenCredentialType?: string;
  onSuccess?: (response: UpsertCredentialsResponse) => void;
}): JSX.Element {
  const { user } = useAuth();
  const [data, setData] = useState<GeneratePendingSshKeyResponse | null>(null);
  const [selectedCredentialType, setSelectedCredentialType] = useState(
    CredentialTypeOptions[0].value
  );

  useEffect(() => {
    if (givenCredentialType) {
      setSelectedCredentialType(givenCredentialType);
    }
  }, [givenCredentialType]);

  const handleCredentialTypeChange = (credentialType: any) => {
    setSelectedCredentialType(credentialType.value);
  };

  const CredentialForms = {
    amazon: AmazonCredentialsForm,
    google: GoogleCredentialsForm,
    ssh: SSHCredentialsForm,
  };

  const SelectedCredentialFormComponent =
    CredentialForms[selectedCredentialType];

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CredentialKeys>({
    mode: "onChange",
    shouldUnregister: true,
  });

  const {
    mutateAsync: createCredential,
    data: createCredentialResponse,
    isPending: isCreateCredentialLoading,
    isSuccess: isCreateCredentialSuccess,
  } = useCreateCredential();

  const checkIsAppending = (type, role) => {
    return (
      (type === CREDENTIAL_TYPES.AMAZON || type === CREDENTIAL_TYPES.SSH) &&
      role !== USER_ROLES.ADMIN
    );
  };

  const handlePayloadFormatting = <T extends Record<string, any>>(
    payload: T
  ): T => {
    const isAppending = checkIsAppending(
      selectedCredentialType,
      user?.userRole
    );
    if (!isAppending) return payload;

    const [key, value] = Object.entries(payload)[0];
    const trimmedValue = Object.fromEntries(
      Object.entries(value).map(([k, v]) => [
        k,
        typeof v === "string" ? v.trim() : v,
      ])
    );

    return {
      ...payload,
      [key]: {
        ...trimmedValue,
        hide: false,
      },
    };
  };

  const handleClickEvent = () => {
    if (selectedCredentialType === CREDENTIAL_TYPES.SSH) {
      fetchGeneratedSSHKeys().then(response => {
        setData(response);
      });
    }
  };

  const handleSaveCredential = async (payload: CredentialKeys) => {
    const submission = handlePayloadFormatting(payload);
    await createCredential(
      removeEmptyStrings(submission) as UpsertCredentialsRequestBody
    );
  };

  const handleClose = () => {
    // Discard a generated key that was not saved. It also expires on the server.
    if (data?.pendingKeyId && !isCreateCredentialSuccess) {
      void cancelGeneratedSSHKey(data.pendingKeyId);
    }
    setOpen(false);
  };

  useEffect(() => {
    if (isCreateCredentialSuccess && createCredentialResponse) {
      onSuccess && onSuccess(createCredentialResponse);
      handleClose();
    }
  }, [isCreateCredentialSuccess, createCredentialResponse]);

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleSaveCredential)(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              New Credential Key
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={handleClose}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Get started by filling in the information below to save your
              credential
            </p>
          </div>
        </div>

        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Credential Type <span className="text-red-400">*</span>
                </label>
                <div className="mt-2">
                  <SelectComponent
                    readOnly={givenCredentialType ? true : false}
                    name="credential-type"
                    options={CredentialTypeOptions}
                    formatOptionLabel={option => (
                      <div className="flex flex-row items-center">
                        <img
                          alt="Credential Logo"
                          className="mr-2 h-5 w-5"
                          src={option.icon}
                        />{" "}
                        {option.label}
                      </div>
                    )}
                    onChange={handleCredentialTypeChange}
                    value={CredentialTypeOptions.find(
                      ({ value }) => value === selectedCredentialType
                    )}
                    enableClear={false}
                  />
                </div>
              </div>

              {SelectedCredentialFormComponent && (
                <SelectedCredentialFormComponent
                  role={user?.userRole}
                  register={register}
                  control={control}
                  errors={errors}
                  setValue={setValue}
                  data={data}
                  mode="create"
                  onClick={handleClickEvent}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="credential-key-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          id="credential-key-save"
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isCreateCredentialLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
}
